import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAppStore } from '@/store'
import { getThemeList, getBackgroundList, themes, backgrounds } from '@/styles/themes'
import { Icon, ICONS } from '@/lib/icons'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

interface ThemePickerProps {
  className?: string
}

export function ThemePicker({ className }: ThemePickerProps) {
  const { t } = useTranslation()
  const { theme, background, setTheme, setBackground } = useAppStore()
  const themeList = getThemeList()
  const backgroundList = getBackgroundList()

  // Group themes by mode
  const lightThemes = themeList.filter(th => th.mode === 'light')
  const darkThemes = themeList.filter(th => th.mode === 'dark')

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon icon={ICONS.palette} width={20} height={20} />
          {t('settings.theme')}
        </CardTitle>
        <CardDescription>
          {t('settings.theme.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* COLOR THEMES */}
        <div className="space-y-4">
          {/* Light themes */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
              <Icon icon={ICONS.sun} width={16} height={16} />
              {t('settings.lightThemes')}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {lightThemes.map((th) => (
                <ThemeCard
                  key={th.name}
                  name={th.name}
                  isSelected={theme === th.name}
                  onClick={() => setTheme(th.name)}
                />
              ))}
            </div>
          </div>

          {/* Dark themes */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
              <Icon icon={ICONS.moon} width={16} height={16} />
              {t('settings.darkThemes')}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {darkThemes.map((th) => (
                <ThemeCard
                  key={th.name}
                  name={th.name}
                  isSelected={theme === th.name}
                  onClick={() => setTheme(th.name)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* BACKGROUND PATTERNS */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
            <Icon icon={ICONS.image} width={16} height={16} />
            {t('settings.background')}
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {backgroundList.map((bg) => (
              <BackgroundCard
                key={bg.name}
                name={bg.name}
                label={t(`settings.background.${bg.name}`, bg.label)}
                sport={bg.sport}
                isSelected={background === bg.name}
                onClick={() => setBackground(bg.name)}
              />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface ThemeCardProps {
  name: string
  isSelected: boolean
  onClick: () => void
}

function ThemeCard({ name, isSelected, onClick }: ThemeCardProps) {
  const { t } = useTranslation()
  const themeData = themes[name]
  
  // Get translated label
  const label = t(`settings.theme.${name}`, themeData?.label || name)
  
  // Extract colors for preview swatch
  const primaryColor = themeData?.primary || 'oklch(0.45 0.15 250)'
  const bgColor = themeData?.background || 'oklch(1 0 0)'
  const fgColor = themeData?.foreground || 'oklch(0.25 0.08 250)'
  
  return (
    <button
      onClick={onClick}
      className={cn(
        'relative flex flex-col items-start p-3 rounded-lg border-2 transition-all',
        'hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        isSelected 
          ? 'border-primary bg-primary/5' 
          : 'border-border hover:border-primary/50'
      )}
    >
      {/* Color preview swatches */}
      <div className="flex gap-1 mb-2">
        <div 
          className="w-6 h-6 rounded-full border border-border"
          style={{ background: bgColor }}
          title="Background"
        />
        <div 
          className="w-6 h-6 rounded-full border border-border"
          style={{ background: primaryColor }}
          title="Primary"
        />
        <div 
          className="w-6 h-6 rounded-full border border-border"
          style={{ background: fgColor }}
          title="Text"
        />
      </div>
      
      {/* Theme name */}
      <span className="text-sm font-medium">{label}</span>
      
      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2">
          <Icon icon={ICONS.checkCircle} width={16} height={16} className="text-primary" />
        </div>
      )}
    </button>
  )
}

interface BackgroundCardProps {
  name: string
  label: string
  sport?: string
  isSelected: boolean
  onClick: () => void
}

function BackgroundCard({ name, label, isSelected, onClick }: BackgroundCardProps) {
  const bg = backgrounds[name]
  
  // Create a mini preview of the background pattern
  const previewStyle: React.CSSProperties = name === 'none' 
    ? { background: 'oklch(0.96 0.01 250)' }
    : { 
        backgroundImage: bg?.preview || bg?.css || 'none',
        backgroundSize: name === 'dots' ? '10px 10px' : 'cover',
        backgroundPosition: 'center',
      }
  
  return (
    <button
      onClick={onClick}
      className={cn(
        'relative flex flex-col items-center p-2 rounded-lg border-2 transition-all',
        'hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        isSelected 
          ? 'border-primary' 
          : 'border-border hover:border-primary/50'
      )}
    >
      {/* Pattern preview */}
      <div 
        className="w-full h-10 rounded-md mb-1 border border-border/50"
        style={previewStyle}
      />
      
      {/* Label */}
      <span className="text-xs font-medium text-center">{label}</span>
      
      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-1 right-1">
          <Icon icon={ICONS.checkCircle} width={14} height={14} className="text-primary" />
        </div>
      )}
    </button>
  )
}
